/**
 * Scope 2 Supplementary Emissions Panel
 *
 * Displays T&D losses, WTT upstream emissions, and biogenic CO2 from Scope 2
 * energy activities. These values are NOT included in the Scope 2 total —
 * they are Scope 3 supplementary disclosures per GHG Protocol.
 */

import { Info, Loader2, Zap, Flame, Leaf } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { SCOPE_COLORS, SCOPE_BG_COLORS } from '../config/scopeBranding';
import type { Scope2SupplementaryTotals } from '../types/emissions';

interface Scope2SupplementaryPanelProps {
  supplementary: Scope2SupplementaryTotals | undefined;
  isLoading: boolean;
}

interface BadgeConfig {
  text: string;
  color: string;
  bg: string;
}

const SCOPE3_BADGE: BadgeConfig = {
  text: 'Scope 3',
  color: SCOPE_COLORS.scope3,
  bg: SCOPE_BG_COLORS.scope3,
};

const MEMO_BADGE: BadgeConfig = {
  text: 'Memo item',
  color: '#92400e', // amber-800
  bg: 'rgba(245, 158, 11, 0.1)',
};

export const Scope2SupplementaryPanel = ({ supplementary, isLoading }: Scope2SupplementaryPanelProps) => {
  const { formatCompact } = useEmissionsFormatter();

  const hasAnyData = supplementary && (
    supplementary.tdCo2eTotal > 0 ||
    supplementary.wttCo2eTotal > 0 ||
    supplementary.wttOfTdCo2eTotal > 0 ||
    supplementary.biogenicCo2eTotal > 0
  );

  const wttTotal = (supplementary?.wttCo2eTotal ?? 0) + (supplementary?.wttOfTdCo2eTotal ?? 0);
  const hasBiogenic = (supplementary?.biogenicCo2eTotal ?? 0) > 0;

  return (
    <Card className='tw:h-full'>
      <CardHeader className='tw:pb-2'>
        <CardTitle className='tw:text-sm tw:font-semibold tw:flex tw:items-center tw:gap-2'>
          Supplementary Disclosures
          <span title='These values are not included in your Scope 2 total. Required separately by GHG Protocol, GRI 305-1, CDP (Q8.2a), and CSRD.'>
            <Info className='tw:size-4 tw:text-muted-foreground tw:shrink-0' />
          </span>
        </CardTitle>
        <CardDescription>
          Not counted in scope totals — required by GHG Protocol, GRI, CDP &amp; CSRD
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='tw:flex tw:items-center tw:justify-center tw:py-6'>
            <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
          </div>
        ) : !hasAnyData ? (
          <div className='tw:flex tw:flex-col tw:items-center tw:py-6 tw:text-muted-foreground'>
            <p className='tw:text-sm'>No supplementary disclosures available</p>
            <p className='tw:text-xs tw:mt-1 tw:text-center tw:max-w-xs'>
              T&amp;D losses, well-to-tank upstream, and biogenic CO₂ will appear here when Scope 2 energy activities are calculated
            </p>
          </div>
        ) : (
          <div className='tw:space-y-3'>
            {(supplementary?.tdCo2eTotal ?? 0) > 0 && (
              <SupplementaryRow
                icon={<Zap className='tw:size-4 tw:text-blue-600' />}
                iconBg='tw:bg-blue-500/10'
                label='Transmission & Distribution Losses'
                badge={{ ...SCOPE3_BADGE, text: 'Scope 3.3' }}
                value={supplementary!.tdCo2eTotal}
                tooltip='Transmission & distribution losses from electricity travelling the grid. Reported separately under Scope 3 category 3.3 per GHG Protocol.'
                format={formatCompact}
              />
            )}

            {wttTotal > 0 && (
              <SupplementaryRow
                icon={<Flame className='tw:size-4 tw:text-orange-600' />}
                iconBg='tw:bg-orange-500/10'
                label='Well-to-Tank Upstream'
                badge={{ ...SCOPE3_BADGE, text: 'Scope 3 upstream' }}
                value={wttTotal}
                tooltip='Well-to-tank upstream emissions from fuel extraction and refining used to generate electricity, including upstream of T&D losses.'
                format={formatCompact}
              />
            )}

            {hasBiogenic && (
              <SupplementaryRow
                icon={<Leaf className='tw:size-4 tw:text-amber-600' />}
                iconBg='tw:bg-amber-500/10'
                label='Biogenic CO₂'
                badge={MEMO_BADGE}
                value={supplementary!.biogenicCo2eTotal}
                tooltip='CO₂ from biogenic sources (e.g. biomass combustion). Excluded from all GHG scope totals per GHG Protocol, but required as a separate disclosure under GRI 305-1 and CSRD.'
                format={formatCompact}
                valueClassName='tw:text-amber-700'
                memoItem
              />
            )}

            <div className='tw:text-xs tw:text-muted-foreground tw:pt-1 tw:border-t tw:border-border'>
              Location-based only. Excluded from all scope totals.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface SupplementaryRowProps {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  badge: BadgeConfig;
  value: number;
  tooltip: string;
  format: (value: number) => { formatted: string; unit: string };
  valueClassName?: string;
  memoItem?: boolean;
}

const SupplementaryRow = ({ icon, iconBg, label, badge, value, tooltip, format, valueClassName, memoItem }: SupplementaryRowProps) => {
  const formatted = format(value);
  return (
    <div className={`tw:flex tw:gap-3 tw:py-2 ${memoItem ? 'tw:border tw:border-amber-100' : ''}`}>
      <div className={`tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:size-8 tw:shrink-0 tw:mt-0.5 ${iconBg}`}>
        {icon}
      </div>
      <div className='tw:flex-1 tw:min-w-0'>
        <div className='tw:flex tw:items-center tw:gap-1.5'>
          <span className='tw:text-sm tw:font-medium tw:leading-snug'>{label}</span>
          <span title={tooltip} className='tw:shrink-0'>
            <Info className='tw:size-3 tw:text-muted-foreground' />
          </span>
        </div>
        <div className='tw:flex tw:items-center tw:justify-between tw:mt-1'>
          <span
            className='tw:inline-flex tw:items-center tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium'
            style={{ color: badge.color, backgroundColor: badge.bg }}
          >
            {badge.text}
          </span>
          <div className='tw:shrink-0'>
            <span className={`tw:text-sm tw:font-semibold ${valueClassName ?? ''}`}>
              {formatted.formatted}
            </span>
            <span className='tw:text-xs tw:text-muted-foreground tw:ml-1'>
              {formatted.unit}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
